import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { BASE_URL, IMG_BASE_URL } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import AddIcon from "@mui/icons-material/Add";
import {
  Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  IconButton, Stack, Switch, Table,
  TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
  TextField, InputAdornment, useTheme, alpha,
} from "@mui/material";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import BannerForm from "./BannerForm";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import { useToast } from "contexts/ToastContext";



const toImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  // remove leading slash if present to avoid double slash
  const cleanPath = path.replace(/^\//, "");
  return `${IMG_BASE_URL}/${cleanPath}`;
};

const fmtDate = (val) => (val ? String(val).split("T")[0] : "—");

const fmtTime = (val) => {
  if (!val) return null;
  return String(val).slice(0, 5); // "HH:MM:SS" → "HH:MM"
};

const isLiveNow = (banner) => {
  if (!banner.status) return false;
  const now      = new Date();
  const today    = now.toISOString().slice(0, 10);
  const nowTime  = now.toTimeString().slice(0, 8);
  const fromDate = String(banner.from_date).slice(0, 10);
  const toDate   = String(banner.to_date).slice(0, 10);
  const fromTime = banner.from_time || "00:00:00";
  const toTime   = banner.to_time   || "23:59:59";
  if (today < fromDate || today > toDate) return false;
  if (today === fromDate && nowTime < fromTime) return false;
  if (today === toDate   && nowTime > toTime)   return false;
  return true;
};

const isExpired = (banner) => {
  if (!banner.to_date) return false;
  return new Date(String(banner.to_date).slice(0, 10)) < new Date(new Date().toISOString().slice(0, 10));
};

const isUpcoming = (banner) => {
  if (!banner.from_date) return false;
  return new Date(String(banner.from_date).slice(0, 10)) > new Date(new Date().toISOString().slice(0, 10));
};

export default function BannerList() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const primary  = theme.palette.primary.main;
  const { showToast, showApiError } = useToast();

  const [banners,    setBanners]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [delDialog,  setDelDialog]  = useState({ open: false, id: null, seq: null });
  const [deleting,   setDeleting]   = useState(false);
  const [modalOpen,  setModalOpen]  = useState(false);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res  = await api.get("/banner/list");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setBanners(data);
      setFiltered(data);
    } catch (err) {
      showApiError(err, "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  // ── search filter ─────────────────────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) { setFiltered(banners); return; }
    setFiltered(
      banners.filter((b) =>
        [b.store?.title_en, b.store?.title_ar, String(b.sequence), fmtDate(b.from_date), fmtDate(b.to_date)]
          .some((v) => v?.toLowerCase().includes(q))
      )
    );
  }, [search, banners]);

  // ── toggle status ─────────────────────────────────────────────────────────
  const handleStatusToggle = async (banner) => {
    setTogglingId(banner.id);
    try {
      await api.put(`/banner/manage/status/${banner.id}`);
      setBanners((prev) =>
        prev.map((b) => b.id === banner.id ? { ...b, status: !b.status } : b)
      );
    } catch (err) {
      showApiError(err, "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/banner/delete/${delDialog.id}`);
      setBanners((prev) => prev.filter((b) => b.id !== delDialog.id));
      setDelDialog({ open: false, id: null, seq: null });
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const liveCount = banners.filter(isLiveNow).length;

  // ── sub-components ────────────────────────────────────────────────────────

  // Status badge — shows computed live state
  const StatusBadge = ({ banner }) => {
    const live     = isLiveNow(banner);
    const expired  = isExpired(banner);
    const upcoming = isUpcoming(banner);

    if (!banner.status) {
      return <Chip label="Inactive" size="small" color="default" variant="outlined" />;
    }
    if (live) {
      return (
        <Chip
          icon={<ThunderboltOutlined style={{ fontSize: 11 }} />}
          label="Live Now"
          size="small"
          color="primary"
          sx={{ fontWeight: 700, fontSize: "0.7rem" }}
        />
      );
    }
    if (expired)  return <Chip label="Expired"  size="small" color="error"   variant="outlined" />;
    if (upcoming) return <Chip label="Upcoming" size="small" color="info"    variant="outlined" />;
    return              <Chip label="Active"    size="small" color="success" variant="outlined" />;
  };

  // Schedule cell — compact date + time chips
  const ScheduleCell = ({ banner }) => {
    const expired  = isExpired(banner);
    const fromTime = fmtTime(banner.from_time);
    const toTime   = fmtTime(banner.to_time);
    return (
      <Stack spacing={0.4}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ minWidth: 28 }}>
            From
          </Typography>
          <Typography variant="body2" fontWeight={600}>{fmtDate(banner.from_date)}</Typography>
          {fromTime && (
            <Chip label={fromTime} size="small" variant="outlined"
              sx={{ fontSize: "0.62rem", height: 18, borderColor: "grey.400" }} />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ minWidth: 28 }}>
            To
          </Typography>
          <Typography variant="body2" fontWeight={600} color={expired ? "error.main" : "text.primary"}>
            {fmtDate(banner.to_date)}
          </Typography>
          {toTime && (
            <Chip label={toTime} size="small" variant="outlined"
              sx={{ fontSize: "0.62rem", height: 18, borderColor: expired ? "error.light" : "grey.400" }} />
          )}
          {expired && (
            <Chip label="Expired" size="small" color="error" sx={{ fontSize: "0.6rem", height: 16 }} />
          )}
        </Stack>
      </Stack>
    );
  };

  // Image thumbnail
  const ImgThumb = ({ url, alt }) => {
    const src = toImgUrl(url);
    if (!src) {
      return (
        <Box sx={{
          width: 90, height: 50, borderRadius: 1,
          bgcolor: "grey.100", border: "1px dashed", borderColor: "grey.300",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Typography variant="caption" color="text.disabled" fontSize={10}>No image</Typography>
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: 90, height: 50, objectFit: "cover",
          borderRadius: 1, border: "1px solid", borderColor: "divider", display: "block",
        }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  };

  return (
    <>
      <PageHeader
        title="Banners"
        subtitle="Manage promotional banners, sequences, and display schedules."
        count={filtered.length}
        titleExtra={
          liveCount > 0 ? (
            <Chip
              icon={<ThunderboltOutlined style={{ fontSize: 10 }} />}
              label={`${liveCount} Live`}
              size="small"
              sx={{ fontWeight: 700, fontSize: "0.68rem", bgcolor: "rgba(31, 91, 64, 0.1)", color: "primary.main" }}
            />
          ) : null
        }
        actionLabel="Add Banner"
        actionIcon={AddIcon}
        onAction={() => setModalOpen(true)}
      />

      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search store, sequence, date…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined style={{ fontSize: 14, color: "#888" }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchBanners} disabled={loading}>
              <ReloadOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <MainCard contentSX={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ScrollableTableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  {["#", "AR Image", "EN Image", "Store", "Seq", "Schedule", "Live Status", "Toggle", "Actions"].map((h) => (
                    <TableCell key={h} sx={{
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      py: 1.5,
                      color: "text.secondary",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        {search ? "No banners match your search" : "No banners found"}
                      </Typography>
                      {!search && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PlusOutlined />}
                          sx={{ mt: 2 }}
                          onClick={() => setModalOpen(true)}
                        >
                          Add First Banner
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((banner, idx) => {
                    const live = isLiveNow(banner);
                    return (
                      <TableRow
                        key={banner.id}
                        hover
                        sx={{
                          verticalAlign: "middle",
                          borderLeft: live ? `3px solid ${primary}` : "3px solid transparent",
                          bgcolor: live ? alpha(primary, 0.03) : "inherit",
                          transition: "background 0.2s",
                        }}
                      >
                        {/* # */}
                        <TableCell sx={{ color: "text.disabled", fontSize: "0.78rem" }}>
                          {idx + 1}
                        </TableCell>

                        {/* AR Image */}
                        <TableCell>
                          <ImgThumb url={banner.imageAr?.attachment_url} alt="AR Banner" />
                        </TableCell>

                        {/* EN Image */}
                        <TableCell>
                          <ImgThumb url={banner.imageEn?.attachment_url} alt="EN Banner" />
                        </TableCell>

                        {/* Store */}
                        <TableCell>
                          {banner.store?.title_en ? (
                            <Stack>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>
                                {banner.store.title_en}
                              </Typography>
                              {banner.store.title_ar && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {banner.store.title_ar}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Chip
                              label="Global"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.68rem" }}
                            />
                          )}
                        </TableCell>

                        {/* Sequence */}
                        <TableCell>
                          <Chip
                            label={banner.sequence ?? 0}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700, minWidth: 32 }}
                          />
                        </TableCell>

                        {/* Schedule */}
                        <TableCell>
                          <ScheduleCell banner={banner} />
                        </TableCell>

                        {/* Live Status badge */}
                        <TableCell>
                          <StatusBadge banner={banner} />
                        </TableCell>

                        {/* Toggle — active/inactive switch */}
                        <TableCell>
                          <Tooltip title={banner.status ? "Click to Deactivate" : "Click to Activate"}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {togglingId === banner.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Switch
                                  checked={!!banner.status}
                                  color="primary"
                                  size="small"
                                  onChange={() => handleStatusToggle(banner)}
                                />
                              )}
                              <Typography
                                variant="caption"
                                color={banner.status ? "primary.main" : "text.disabled"}
                                fontWeight={600}
                              >
                                {banner.status ? "ON" : "OFF"}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <TableActions>
                            <TableActionButton
                              action="edit"
                              title="Edit Banner"
                              onClick={() => navigate(`/banner/edit/${banner.id}`)}
                            />
                            <TableActionButton
                              action="delete"
                              title="Delete Banner"
                              onClick={() =>
                                setDelDialog({ open: true, id: banner.id, seq: banner.sequence })
                              }
                            />
                          </TableActions>
                        </TableCell>

                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        )}
      </MainCard>

      {/* ── Delete Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, seq: null })}
      >
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>Banner #{delDialog.seq}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDelDialog({ open: false, id: null, seq: null })}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : <DeleteOutlined />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Banner Form Modal ───────────────────────────────────────────── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h4" fontWeight={700}>Create New Banner</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the banner images, store, and display schedule.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <BannerForm 
            isModal={true} 
            onSuccess={() => {
              setModalOpen(false);
              fetchBanners();
            }} 
          />
        </DialogContent>
      </Dialog>
    </>
  );
}